import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import WhatsAppButton from "./components/WhatsAppButton";
import ScrollToTop from "./components/ScrollToTop";
import MustChangePasswordModal from "./components/auth/MustChangePasswordModal";
import { trackMetaEvent } from "./lib/meta";

// Pages
import HomePage from "./pages/HomePage";
import TemplesPage from "./pages/TemplesPage";
import TempleDetailPage from "./pages/TempleDetailPage";
import StorePage from "./pages/StorePage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentPage from "./pages/PaymentPage";
import OrderDetailsPage from "./pages/OrderDetailsPage";
import DashboardPage from "./pages/DashboardPage";
import TrackingPage from "./pages/TrackingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import TravelPage from "./pages/TravelPage";
import PujaBookingForm from "./components/forms/PujaBookingForm";
import PujasPage from "./pages/PujasPage";
import PujaDetailPage from "./pages/PujaDetailPage";
import OfferingsPage from "./pages/OfferingsPage";

// Admin Pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminTemples from "./pages/admin/AdminTemples";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminProductAdd from "./pages/admin/AdminProductAdd";
import AdminProductEdit from "./pages/admin/AdminProductEdit";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminFaqs from "./pages/admin/AdminFaqs";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPujas from "./pages/admin/AdminPujas";
import AdminPujaAdd from "./pages/admin/AdminPujaAdd";
import AdminPujaEdit from "./pages/admin/AdminPujaEdit";
import AdminBhets from "./pages/admin/AdminBhets";
import AdminOfflineInterests from "./pages/admin/AdminOfflineInterests";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminCoupons from "./pages/admin/AdminCoupons";

// Router component to handle OAuth callback detection
const MetaPageView = () => {
  const location = useLocation();
  const currentUrl = `${location.pathname}${location.search}`;
  const previousUrl = useRef(null);

  useEffect(() => {
    if (currentUrl !== previousUrl.current) {
      if (!location.pathname.startsWith('/admin')
          && location.pathname !== '/auth/callback'
          && location.pathname !== '/api/auth/google/callback') {
        trackMetaEvent('PageView');
      }
      previousUrl.current = currentUrl;
    }
  }, [currentUrl, location.pathname]);

  return null;
};

const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id - handle OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/temples" element={<TemplesPage />} />
      <Route path="/temples/:templeId" element={<TempleDetailPage />} />
      <Route path="/temples/:templeId/book/:pujaId" element={<PujaBookingForm />} />
      <Route path="/store" element={<StorePage />} />
      <Route path="/products" element={<StorePage />} />
      <Route path="/store/:productId" element={<ProductDetailPage />} />
      <Route path="/products/:productId" element={<ProductDetailPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/payment/:orderId" element={<PaymentPage />} />
      <Route path="/orders/:orderId" element={<OrderDetailsPage />} />
      <Route path="/order-details/:orderId" element={<OrderDetailsPage />} />
      <Route path="/order-success/:orderId" element={<OrderDetailsPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/orders" element={<DashboardPage />} />
      <Route path="/tracking/:bookingId" element={<TrackingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/api/auth/google/callback" element={<AuthCallback />} />
      <Route path="/travel" element={<TravelPage />} />
      <Route path="/pujas" element={<PujasPage />} />
      <Route path="/puja" element={<PujasPage />} />
      <Route path="/puja/:slug" element={<PujaDetailPage />} />
      <Route path="/pujas/:pujaId" element={<PujaDetailPage />} />
      <Route path="/offerings" element={<OfferingsPage />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
      <Route path="/admin/bookings" element={<AdminLayout><AdminBookings /></AdminLayout>} />
      <Route path="/admin/orders" element={<AdminLayout><AdminOrders /></AdminLayout>} />
      <Route path="/admin/payments" element={<AdminLayout><AdminPayments /></AdminLayout>} />
      <Route path="/admin/temples" element={<AdminLayout><AdminTemples /></AdminLayout>} />
      <Route path="/admin/products" element={<AdminLayout><AdminProducts /></AdminLayout>} />
      <Route path="/admin/products/new" element={<AdminLayout><AdminProductAdd /></AdminLayout>} />
      <Route path="/admin/products/edit/:productId" element={<AdminLayout><AdminProductEdit /></AdminLayout>} />
      <Route path="/admin/categories" element={<AdminLayout><AdminCategories /></AdminLayout>} />
      <Route path="/admin/faqs" element={<AdminLayout><AdminFaqs /></AdminLayout>} />
      <Route path="/admin/testimonials" element={<AdminLayout><AdminTestimonials /></AdminLayout>} />
      <Route path="/admin/users" element={<AdminLayout><AdminUsers /></AdminLayout>} />
      <Route path="/admin/pujas" element={<AdminLayout><AdminPujas /></AdminLayout>} />
      <Route path="/admin/pujas/new" element={<AdminLayout><AdminPujaAdd /></AdminLayout>} />
      <Route path="/admin/pujas/edit/:pujaId" element={<AdminLayout><AdminPujaEdit /></AdminLayout>} />
      <Route path="/admin/bhets" element={<AdminLayout><AdminBhets /></AdminLayout>} />
      <Route path="/admin/offline-interests" element={<AdminLayout><AdminOfflineInterests /></AdminLayout>} />
      <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
      <Route path="/admin/coupons" element={<AdminLayout><AdminCoupons /></AdminLayout>} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <MetaPageView />
        <LanguageProvider>
          <AuthProvider>
            <CartProvider>
              <MustChangePasswordModal />
              <AppRouter />
              <WhatsAppButton />
              <Toaster position="top-right" richColors />
            </CartProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
